import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getSupabaseClient } from '@/lib/supabase';
import Footer from '@/components/feature/Footer';
import SignupRequiredModal from '@/components/feature/SignupRequiredModal';
import type { ParsedProduct } from '@/hooks/useProducts';
import type { ImageSearchResultItem } from '@/mocks/imageSearch';
import {
  validateImageFile,
  uploadAndCreateSearchJob,
  startImageSearchPolling,
  simulateImageSearch,
  checkSupabaseConnectivity,
} from '@/lib/imageSearchService';

// ─── Adapter: ImageSearchResultItem → ParsedProduct ───

function itemToParsedProduct(item: ImageSearchResultItem): ParsedProduct {
  const availabilityStatus =
    item.availability_status === 'available' ||
    item.availability_status === 'limited_stock' ||
    item.availability_status === 'check_availability' ||
    item.availability_status === 'made_to_order'
      ? item.availability_status
      : null;

  return {
    id: item.id,
    name: item.name,
    sku: item.sku,
    category: item.category,
    status: 'active',
    imageUrl: item.image_url,
    createdAt: '',
    updatedAt: '',
    capacity: item.capacity || null,
    materialHint: item.material || null,
    mappedCategory: item.category || null,
    categoryL2Code: item.category || null,
    categoryL1Code: null,
    availabilityStatus,
    availabilityDisplay: item.availability_display || null,
    availabilityBadgeType: item.availability_badge_type || null,
    availabilityNote: item.price_note || null,
    moq: null,
    moqDisplay: item.moq_display || null,
    leadTimeMinDays: null,
    leadTimeMaxDays: null,
    leadTimeDisplay: item.lead_time_display || null,
    productKind: null,
    deliverDays: null,
    capacityMl: null,
    capacityMlTag: null,
    moqTag: null,
    similarity: item.similarity,
    displayPriceText: item.display_price
      ? `${item.display_currency || 'USD'} ${item.display_price.toFixed(2)}`
      : null,
    samplePriceText: null,
    tags: null,
  };
}

// ─── Availability Badge ───

function AvailabilityBadge({ status }: { status: ParsedProduct['availabilityStatus'] }) {
  if (!status) return null;

  const config: Record<string, { cls: string; icon: string; label: string }> = {
    available: { cls: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: 'ri-check-line', label: 'Available' },
    limited_stock: { cls: 'bg-amber-50 border-amber-200 text-amber-700', icon: 'ri-alarm-warning-line', label: 'Limited' },
    check_availability: { cls: 'bg-slate-100 border-slate-200 text-slate-600', icon: 'ri-question-line', label: 'Check' },
    made_to_order: { cls: 'bg-ink-50 border-ink-200 text-ink-600', icon: 'ri-time-line', label: 'MTO' },
  };

  const c = config[status];
  if (!c) return null;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-semibold rounded ${c.cls}`}>
      <i className={c.icon} />
      {c.label}
    </span>
  );
}

// ─── Similarity Badge ───

function SimilarityBadge({ value }: { value: number }) {
  const percent = Math.round(value * 100);
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-blue-200 bg-blue-50 text-blue-700 text-[10px] font-bold rounded">
      <i className="ri-swap-line text-[10px]" />
      {percent}%
    </span>
  );
}

// ─── Product Card (adapted from ProductResults) ───

function SearchResultCard({ product }: { product: ParsedProduct }) {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className="group bg-white rounded-lg border border-ink-100 overflow-hidden hover:border-ink-300 transition-all duration-200 flex flex-col cursor-pointer hover:-translate-y-0.5"
      onClick={() => navigate(`/products/${product.id}`)}
    >
      <div className="relative aspect-square bg-white overflow-hidden">
        {!imgError && product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-contain p-4 group-hover:scale-[1.02] transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-cream-50 p-4">
            <i className="ri-box-3-line text-4xl text-ink-200 mb-2" />
            <span className="text-xs text-ink-300">No image</span>
          </div>
        )}

        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
          <span className="text-[10px] font-medium text-ink-400">SKU: {product.sku ?? '—'}</span>
          {product.similarity != null && <SimilarityBadge value={product.similarity} />}
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h4 className="text-sm font-semibold text-ink-700 line-clamp-2 mb-1 leading-snug min-h-[2.5rem]">
          {product.name}
        </h4>

        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-ink-400 mt-1.5 mb-2">
          {product.capacity && (
            <>
              <span className="inline-flex items-center gap-x-1 whitespace-nowrap">
                <span className="text-ink-500 font-medium">용량:</span>
                <span className="text-ink-600">{product.capacity}</span>
              </span>
              <span className="text-ink-200 select-none">|</span>
            </>
          )}
          {product.materialHint && (
            <>
              <span className="inline-flex items-center gap-x-1 whitespace-nowrap">
                <span className="text-ink-500 font-medium">재질:</span>
                <span className="text-ink-600">{product.materialHint}</span>
              </span>
              <span className="text-ink-200 select-none">|</span>
            </>
          )}
          {product.moqDisplay && (
            <span className="inline-flex items-center gap-x-1 whitespace-nowrap">
              <span className="text-ink-600">{product.moqDisplay}</span>
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs mt-auto">
          <AvailabilityBadge status={product.availabilityStatus} />
          {product.displayPriceText && (
            <span className="text-ink-700 font-bold text-xs">{product.displayPriceText}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───

export default function ImageSearchPage() {
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Job states
  const [isSearching, setIsSearching] = useState(false);
  const [jobStatus, setJobStatus] = useState<'idle' | 'uploading' | 'pending' | 'processing' | 'completed' | 'failed'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showSignupModal, setShowSignupModal] = useState(false);

  // Results
  const [results, setResults] = useState<ParsedProduct[]>([]);
  const [resultMessage, setResultMessage] = useState('');

  const pollStopRef = useRef<{ stop: () => void } | null>(null);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobIdRef = useRef<string | null>(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pollStopRef.current) { pollStopRef.current.stop(); pollStopRef.current = null; }
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, []);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    };
  }, [uploadedPreviewUrl]);

  const cleanupTimers = useCallback(() => {
    if (pollStopRef.current) { pollStopRef.current.stop(); pollStopRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
  }, []);

  const handleFile = useCallback((file: File) => {
    const validation = validateImageFile(file);
    if (!validation.ok) {
      setFileError(validation.message);
      return;
    }

    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedFile(file);
    setUploadedPreviewUrl(URL.createObjectURL(file));
    setFileError(null);
    setResults([]);
    setResultMessage('');
    setSearchError(null);
    setJobStatus('idle');
  }, [uploadedPreviewUrl]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = () => {
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedFile(null);
    setUploadedPreviewUrl(null);
    setFileError(null);
    setResults([]);
    setResultMessage('');
    setSearchError(null);
    setJobStatus('idle');
  };

  const handleSearch = async () => {
    if (!uploadedFile) return;

    // 비로그인 사용자는 회원가입 유도 모달을 띄운다
    if (!user) {
      setShowSignupModal(true);
      return;
    }

    const supabase = getSupabaseClient();

    // If Supabase client doesn't exist at all, use mock
    if (!supabase) {
      runMockSearch();
      return;
    }

    // Verify Supabase is actually reachable before attempting real flow
    const isSupabaseAlive = await checkSupabaseConnectivity();
    if (!isSupabaseAlive) {
      console.warn('[ImageSearch] Supabase not reachable, falling back to mock');
      runMockSearch();
      return;
    }

    // ─── Real flow: Upload → Create job → Poll ───
    setIsSearching(true);
    setJobStatus('uploading');
    setStatusMessage('이미지 업로드 중...');
    setProgressPercent(0);
    setSearchError(null);
    setElapsed(0);
    setResults([]);
    setResultMessage('');

    const startTime = Date.now();
    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    try {
      const job = await uploadAndCreateSearchJob(uploadedFile, company?.id || null);
      jobIdRef.current = job.id;
      setJobStatus('pending');
      setStatusMessage('이미지 분석 대기 중...');
      setProgressPercent(0);

      // Start polling via REST API (immediate first poll, then every 2s, max 2min)
      const { stop } = startImageSearchPolling(job.id, {
        onProgress: (status, percent, message) => {
          if (status === 'processing') setJobStatus('processing');
          setProgressPercent(percent);
          setStatusMessage(message);
        },
        onCompleted: (items, message) => {
          cleanupTimers();
          const parsed = items.map(itemToParsedProduct);
          setResults(parsed);
          setResultMessage(message);
          setJobStatus('completed');
          setProgressPercent(100);
          setStatusMessage('');
          setIsSearching(false);
        },
        onFailed: (errorMessage) => {
          cleanupTimers();
          setSearchError(errorMessage);
          setJobStatus('failed');
          setIsSearching(false);
        },
        onTimeout: () => {
          cleanupTimers();
          setSearchError('검색 시간이 초과되었습니다. 다시 시도해주세요.');
          setJobStatus('failed');
          setIsSearching(false);
        },
      });
      pollStopRef.current = { stop };
    } catch (err) {
      cleanupTimers();
      // If the real flow fails (e.g. Storage upload error), fall back to mock
      console.warn('[ImageSearch] Real flow failed, falling back to mock:', err instanceof Error ? err.message : String(err));
      runMockSearch();
    }
  };

  const runMockSearch = async () => {
    setIsSearching(true);
    setJobStatus('processing');
    setStatusMessage('이미지 분석 중...');
    setProgressPercent(0);
    setSearchError(null);
    setElapsed(0);

    const startTime = Date.now();
    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    try {
      const { job } = await simulateImageSearch();
      if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }

      const resultJson = job.result_json as { items: ImageSearchResultItem[]; status_message?: string };
      const items: ImageSearchResultItem[] = resultJson.items || [];
      const parsed = items.map(itemToParsedProduct);

      setResults(parsed);
      setResultMessage(resultJson.status_message || `${items.length}개의 유사 상품을 찾았습니다.`);
      setJobStatus('completed');
      setProgressPercent(100);
      setStatusMessage('');
      setIsSearching(false);
    } catch {
      setSearchError('이미지 검색 중 오류가 발생했습니다.');
      setJobStatus('failed');
      setIsSearching(false);
      if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    }
  };

  const handleReset = () => {
    cleanupTimers();
    setUploadedFile(null);
    setUploadedPreviewUrl(null);
    setFileError(null);
    setResults([]);
    setResultMessage('');
    setSearchError(null);
    setIsSearching(false);
    setJobStatus('idle');
    setStatusMessage('');
    setProgressPercent(0);
    setElapsed(0);
    jobIdRef.current = null;
  };

  const canSearch = uploadedFile && !isSearching;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* ─── Header ─── */}
      <div className="border-b border-ink-100">
        <div className="w-full px-4 md:px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 rounded-lg border border-ink-200 flex items-center justify-center hover:bg-cream-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-ink-500" />
            </button>
            <div>
              <h1 className="text-lg font-extrabold text-ink-700">이미지 검색</h1>
              <p className="text-xs text-ink-400">용기 사진을 올리면 비슷한 카탈로그 상품을 찾아드립니다</p>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Signup Required Modal ─── */}
      <SignupRequiredModal
        open={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        description="이미지 검색은 회원 전용 서비스예요.\n무료로 가입하고 AI가 찾아주는 유사 상품을 바로 확인해보세요."
      />

      {/* ─── Content ─── */}
      <div className="flex-1">
        <div className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-8">
          {/* ─── Upload Zone ─── */}
          <div className="max-w-3xl mx-auto mb-10">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-2xl transition-all duration-200 ${
                dragOver
                  ? 'border-warm-400 bg-warm-50/50'
                  : uploadedFile
                    ? 'border-emerald-300 bg-emerald-50/30'
                    : 'border-ink-200 bg-cream-50/30 hover:border-ink-300 hover:bg-cream-50/50'
              }`}
            >
              <div className="p-8 md:p-10 flex flex-col items-center">
                {uploadedPreviewUrl ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative">
                      <div className="w-48 h-48 rounded-xl overflow-hidden border border-ink-200 bg-white">
                        <img
                          src={uploadedPreviewUrl}
                          alt="업로드된 이미지"
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                      {!isSearching && jobStatus !== 'completed' && (
                        <button
                          onClick={handleRemoveFile}
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center hover:bg-rose-600 transition-colors cursor-pointer shadow-sm"
                          title="이미지 제거"
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-sm font-bold text-emerald-700 flex items-center gap-1.5">
                        <i className="ri-check-line text-emerald-500" />
                        {uploadedFile.name}
                      </p>
                      <p className="text-[11px] text-ink-400">
                        {(uploadedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    {canSearch && (
                      <button
                        onClick={handleSearch}
                        className="px-6 py-2.5 bg-ink-700 text-white text-sm font-bold rounded-xl hover:bg-ink-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
                      >
                        <i className="ri-search-line" />
                        검색 시작
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-2xl bg-cream-100 flex items-center justify-center mb-4">
                      <i className="ri-image-add-line text-3xl text-ink-300" />
                    </div>
                    <p className="text-sm font-bold text-ink-600 mb-1">
                      용기 이미지를 드래그하거나 클릭하여 업로드
                    </p>
                    <p className="text-xs text-ink-400 mb-4">JPG, PNG, WebP / 최대 10MB</p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-5 py-2.5 border-2 border-ink-200 text-ink-600 text-sm font-bold rounded-xl hover:bg-white hover:border-ink-300 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-upload-line" />
                      파일 선택
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </>
                )}

                {fileError && (
                  <div className="mt-4 px-4 py-2.5 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2">
                    <i className="ri-error-warning-line text-rose-500 text-sm shrink-0 mt-0.5" />
                    <p className="text-xs text-rose-600">{fileError}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ─── Search Status ─── */}
          {isSearching && (
            <div className="max-w-2xl mx-auto mb-10">
              <div className="bg-gray-100 rounded-xl px-6 py-5 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center shrink-0">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-ink-700">
                      {jobStatus === 'uploading' ? '이미지 업로드 중' : '이미지 분석 중'}
                    </p>
                    <p className="text-[11px] text-ink-400 mt-0.5">{statusMessage || '잠시만 기다려주세요...'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-gray-300 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gray-500 rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <span className="text-[11px] text-ink-400 font-mono shrink-0">
                    {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ─── Error ─── */}
          {searchError && !isSearching && (
            <div className="max-w-2xl mx-auto mb-10">
              <div className="bg-rose-50 border border-rose-200 rounded-xl px-6 py-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
                    <i className="ri-error-warning-line text-rose-500 text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-rose-600">검색 실패</p>
                    <p className="text-xs text-rose-500 mt-0.5">{searchError}</p>
                    <button
                      onClick={handleReset}
                      className="mt-3 px-5 py-2 text-xs font-bold text-rose-600 border-2 border-rose-300 rounded-lg hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                    >
                      <i className="ri-refresh-line" />
                      다시 시도
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── Results ─── */}
          {jobStatus === 'completed' && results.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-base font-extrabold text-ink-700">검색 결과</h2>
                  <p className="text-xs text-ink-400 mt-1">{resultMessage}</p>
                </div>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 text-xs font-bold text-ink-600 border border-ink-200 rounded-lg hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  <i className="ri-refresh-line" />
                  새 검색
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {results.map((product) => (
                  <SearchResultCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          )}

          {/* ─── Empty Results ─── */}
          {jobStatus === 'completed' && results.length === 0 && (
            <div className="max-w-md mx-auto text-center py-16">
              <div className="w-16 h-16 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-3xl text-ink-300" />
              </div>
              <h3 className="text-lg font-bold text-ink-700 mb-2">유사한 상품이 없습니다</h3>
              <p className="text-sm text-ink-400 mb-6">{resultMessage || '다른 이미지로 다시 시도하거나 카탈로그를 둘러보세요.'}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleReset}
                  className="px-5 py-2.5 border-2 border-ink-300 text-ink-600 text-sm font-bold rounded-xl hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  다른 이미지로 검색
                </button>
                <a
                  href="/packaging"
                  className="px-5 py-2.5 bg-ink-700 text-white text-sm font-bold rounded-xl hover:bg-ink-600 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2"
                >
                  <i className="ri-store-2-line" />
                  카탈로그 둘러보기
                </a>
              </div>
            </div>
          )}

          {/* ─── Initial Empty State ─── */}
          {!isSearching && jobStatus === 'idle' && !uploadedFile && (
            <div className="max-w-2xl mx-auto mt-4 text-center">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-cream-50/60 border border-ink-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-xl bg-white border border-ink-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-camera-line text-ink-500 text-lg" />
                  </div>
                  <p className="text-xs font-bold text-ink-600 mb-1">사진 찍기</p>
                  <p className="text-[10px] text-ink-400">찾고 싶은 용기 사진을 준비하세요</p>
                </div>
                <div className="bg-cream-50/60 border border-ink-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-xl bg-white border border-ink-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-upload-cloud-line text-ink-500 text-lg" />
                  </div>
                  <p className="text-xs font-bold text-ink-600 mb-1">업로드</p>
                  <p className="text-[10px] text-ink-400">위 영역에 드래그하거나 클릭하세요</p>
                </div>
                <div className="bg-cream-50/60 border border-ink-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-xl bg-white border border-ink-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-stack-line text-ink-500 text-lg" />
                  </div>
                  <p className="text-xs font-bold text-ink-600 mb-1">결과 확인</p>
                  <p className="text-[10px] text-ink-400">AI가 비슷한 카탈로그 상품을 찾아줍니다</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}